import { Dialog, DialogContent, IconButton, InputBase } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef, useState, useCallback } from "react";
import "./FontSelectorModal.scss";
import TrueIcon from "../../../../components/icons/TrueIcon";
import SearchIcon from "../../../../components/icons/SearchIcon";
import { BrandApi } from "../../../../utils/api/Brand";

export interface FontItem {
  value: string;
  label: string;
  family: string;
}

interface FontSelectorModalProps {
  open: boolean;
  onClose: () => void;
  value: string;
  onChange: (
    value: string,
    font?: FontItem,
    source?: "brand-fonts" | "system-fonts" | null,
  ) => void;
  customFonts: FontItem[];
  systemFonts?: FontItem[];
  customFontSectionLabel?: string;

  brandId?: string;
  agencyId?: string;
  fontsFor?: "brand" | "agency";
  onSystemFontSelected?: (font: FontItem) => void;
  onSystemFontsLoaded?: (fonts: FontItem[]) => void;
}

const PAGE_SIZE = 40;

const FontSelectorModal = ({
  open,
  onClose,
  value,
  onChange,
  customFonts,
  customFontSectionLabel = "Custom fonts",
  brandId,
  agencyId,
  fontsFor,
  onSystemFontSelected,
  onSystemFontsLoaded,
}: FontSelectorModalProps) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [systemFontsList, setSystemFontsList] = useState<FontItem[]>([]);
  const [customFontsList, setCustomFontsList] =
    useState<FontItem[]>(customFonts);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isCustomLoading, setIsCustomLoading] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!debouncedSearch) {
      setCustomFontsList(customFonts);
    }
  }, [customFonts, debouncedSearch]);

  const injectFontStylesAndWait = useCallback(
    async (fontsArray: any[]): Promise<void> => {
      const cdnBaseUrl = (process.env.REACT_APP_CDN_BASE_URL || "").replace(
        /\/$/,
        "",
      );
      const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(
        /\/$/,
        "",
      );

      let css = "";
      const loadPromises: Promise<any>[] = [];

      fontsArray.forEach((font: any) => {
        if (!font.font_family) return;

        let url = "";
        if (font.woff2_font_storage_key?.trim()) {
          url = `${cdnBaseUrl}/${font.woff2_font_storage_key.replace(/^\//, "")}`;
        } else if (font.woff2_font_file_url?.trim()) {
          url = `${apiBaseUrl}/${font.woff2_font_file_url.replace(/^\//, "")}`;
        }
        if (!url) return;

        css += `
          @font-face {
            font-family: "${font.font_family}";
            src: url("${url}") format("woff2");
            font-weight: ${font.font_weight || "400"};
            font-style: ${font.is_italic ? "italic" : "normal"};
            font-display: swap;
          }
        `;

        try {
          const ff = new FontFace(font.font_family, `url(${url})`, {
            weight: font.font_weight || "400",
            style: font.is_italic ? "italic" : "normal",
          });

          const promise = ff
            .load()
            .then((loaded) => {
              document.fonts.add(loaded);
              return loaded;
            })
            .catch((err) => {
              console.warn(`Failed to load font: ${font.font_family}`, err);
              return null;
            });

          loadPromises.push(promise);
        } catch (err) {
          console.warn(`Failed to create FontFace for: ${font.font_family}`);
        }
      });

      let styleTag = document.getElementById(
        "brand-font-selector-modal-styles",
      ) as HTMLStyleElement;
      if (!styleTag) {
        styleTag = document.createElement("style");
        styleTag.id = "brand-font-selector-modal-styles";
        document.head.appendChild(styleTag);
      }
      styleTag.innerHTML += css;

      await Promise.allSettled(loadPromises);

      try {
        await (document as any).fonts?.ready;
      } catch {
        // Ignore if not supported
      }
    },
    [],
  );

  const fetchCustomFonts = useCallback(
    async (searchTerm: string) => {
      if (!fontsFor || (!brandId && !agencyId)) {
        const filtered = searchTerm
          ? customFonts.filter((f) =>
              f.label.toLowerCase().includes(searchTerm.toLowerCase()),
            )
          : customFonts;
        setCustomFontsList(filtered);
        return;
      }

      try {
        setIsCustomLoading(true);

        const params: any = {
          fonts_for: fontsFor,
        };
        if (searchTerm) params.search = searchTerm;
        if (fontsFor === "brand" && brandId) params.brand_id = brandId;
        if (fontsFor === "agency" && agencyId) params.agency_id = agencyId;

        const res = await BrandApi.GetFonts(params);

        if (res?.success && res.data) {
          let fontsArray: any[] = [];

          if (Array.isArray(res.data)) {
            fontsArray = res.data;
          } else if (Array.isArray(res.data?.docs)) {
            fontsArray = res.data.docs;
          }

          const validFonts = fontsArray.filter((f: any) => {
            if (
              f.woff2_font_storage_key &&
              f.woff2_font_storage_key.trim() !== ""
            ) {
              return true;
            }

            if (Array.isArray(f.variants) && f.variants.length > 0) {
              return f.variants.some(
                (v: any) =>
                  v.woff2_font_storage_key &&
                  v.woff2_font_storage_key.trim() !== "",
              );
            }

            return false;
          });

          const fontsForInjection: any[] = [];
          validFonts.forEach((font: any) => {
            if (
              font.woff2_font_storage_key &&
              font.woff2_font_storage_key.trim() !== ""
            ) {
              fontsForInjection.push(font);
            }
            if (Array.isArray(font.variants)) {
              font.variants.forEach((v: any) => {
                if (
                  v.woff2_font_storage_key &&
                  v.woff2_font_storage_key.trim() !== ""
                ) {
                  fontsForInjection.push({
                    ...v,
                    font_family: font.font_family,
                  });
                }
              });
            }
          });

          await injectFontStylesAndWait(fontsForInjection);

          const mapped: FontItem[] = validFonts.map((font: any) => ({
            label: font.font_family,
            value: font._id,
            family: font.font_family,
          }));

          setCustomFontsList(mapped);
        } else {
          setCustomFontsList([]);
        }
      } catch (err) {
        console.error("Error fetching custom fonts:", err);
        setCustomFontsList([]);
      } finally {
        setIsCustomLoading(false);
      }
    },
    [fontsFor, brandId, agencyId, customFonts, injectFontStylesAndWait],
  );

  const fetchSystemFonts = useCallback(
    async (pageNum: number, searchTerm: string, append: boolean) => {
      try {
        if (append) setIsLoadingMore(true);
        else setIsInitialLoading(true);

        const res = await BrandApi.ListSystemFonts({
          page: pageNum,
          limit: PAGE_SIZE,
          search: searchTerm,
        });

        if (res?.success && res?.data?.data?.docs) {
          const docs = res.data.data.docs;

          const validFonts = docs.filter(
            (f: any) =>
              (f.woff2_font_storage_key &&
                f.woff2_font_storage_key.trim() !== "") ||
              (f.woff2_font_file_url && f.woff2_font_file_url.trim() !== ""),
          );

          await injectFontStylesAndWait(validFonts);

          const mapped: FontItem[] = validFonts.map((font: any) => ({
            label: font.font_family,
            value: font._id,
            family: font.font_family,
          }));

          if (append) {
            setSystemFontsList((prev) => [...prev, ...mapped]);
          } else {
            setSystemFontsList(mapped);
          }

          onSystemFontsLoaded?.(mapped);

          const more =
            res.data.data.hasMore ??
            res.data.data.page < res.data.data.totalPages;
          setHasMore(more);
        }
      } catch (err) {
        console.error("Error fetching system fonts:", err);
      } finally {
        setIsInitialLoading(false);
        setIsLoadingMore(false);
      }
    },
    [injectFontStylesAndWait, onSystemFontsLoaded],
  );

  useEffect(() => {
    if (!open) return;

    setPage(1);
    setSystemFontsList([]);
    setHasMore(true);

    fetchCustomFonts(debouncedSearch);
    fetchSystemFonts(1, debouncedSearch, false);
  }, [open, debouncedSearch, fetchSystemFonts, fetchCustomFonts]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setDebouncedSearch("");
    }
  }, [open]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || isLoadingMore || isInitialLoading || !hasMore)
      return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const threshold = 100;

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchSystemFonts(nextPage, debouncedSearch, true);
    }
  }, [
    page,
    hasMore,
    isLoadingMore,
    isInitialLoading,
    debouncedSearch,
    fetchSystemFonts,
  ]);

  const renderFontItem = (font: FontItem) => (
    <div
      key={font.value}
      className="font-item"
      onClick={() => {
        onChange(font.value, font, "brand-fonts");
        onClose();
      }}
    >
      <span style={{ fontFamily: font.family }}>{font.label}</span>
      {value === font.value && (
        <div className="selected-tick-box">
          <TrueIcon />
        </div>
      )}
    </div>
  );

  const renderSystemFontItem = (font: FontItem) => (
    <div
      key={font.value}
      className="font-item"
      onClick={() => {
        onChange(font.value, font, "system-fonts");
        onSystemFontSelected?.(font);
        onClose();
      }}
    >
      <span style={{ fontFamily: font.family }}>{font.label}</span>
      {value === font.value && (
        <div className="selected-tick-box">
          <TrueIcon />
        </div>
      )}
    </div>
  );

  const showFullLoader = isInitialLoading || isCustomLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ className: "font-dialog-paper" }}
    >
      <DialogContent className="font-modal">
        <div className="modal-header">
          <span>Select fonts</span>
          <IconButton onClick={onClose} className="close-btn" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="search-box">
          <SearchIcon />
          <InputBase
            placeholder="Search fonts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <div
          className="font-list-container"
          ref={scrollRef}
          onScroll={handleScroll}
        >
          {showFullLoader ? (
            <div className="font-list-loading">
              <div className="conic-spinner-inline" />
              <p>
                {debouncedSearch ? "Searching fonts..." : "Loading fonts..."}
              </p>
            </div>
          ) : (
            <>
              {!debouncedSearch && (
                <div
                  className="font-item"
                  onClick={() => {
                    onChange("design_default");
                    onClose();
                  }}
                >
                  <span>Design default</span>
                  {value === "design_default" && (
                    <div className="selected-tick-box">
                      <TrueIcon />
                    </div>
                  )}
                </div>
              )}

              {customFontsList.length > 0 && (
                <>
                  <div className="select-section">{customFontSectionLabel}</div>
                  {customFontsList.map(renderFontItem)}
                </>
              )}

              {systemFontsList.length > 0 && (
                <div className="select-section">System fonts</div>
              )}

              {systemFontsList.map(renderSystemFontItem)}

              {systemFontsList.length === 0 && customFontsList.length === 0 && (
                <div className="font-list-empty">
                  <p>
                    {debouncedSearch
                      ? `No fonts found matching "${debouncedSearch}"`
                      : "No fonts available"}
                  </p>
                </div>
              )}

              {isLoadingMore && (
                <div className="font-list-load-more">
                  <div className="conic-spinner" />
                  <span>Loading more fonts...</span>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FontSelectorModal;
